import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from '@/lib/motion';
import { X, Phone, Clock, User, CheckCircle, CircleNotch } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Time slots for callback preference - shorter labels for mobile
const timeSlots = [
  { value: 'morning', label: '9AM-12PM', fullLabel: 'Morning (9AM - 12PM)' },
  { value: 'afternoon', label: '12PM-4PM', fullLabel: 'Afternoon (12PM - 4PM)' },
  { value: 'evening', label: '4PM-7PM', fullLabel: 'Evening (4PM - 7PM)' },
  { value: 'asap', label: 'ASAP', fullLabel: 'As soon as possible' },
];

export function CallbackPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    preferredTime: 'asap',
    notes: '',
  });

  useEffect(() => {
    // Don't show popup on admin pages or specific landing pages
    const excludedPaths = ['/admin', '/quote', '/booking', '/collaborate', '/rfq'];
    if (excludedPaths.some(path => window.location.pathname.startsWith(path))) {
      setIsDismissed(true);
      return;
    }
    
    // Check if already dismissed in this session
    const dismissed = sessionStorage.getItem('callback_popup_dismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    // Show popup after 45 seconds (increased from 30)
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 45000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('callback_popup_dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/callback-requests`, {
        name: formData.name,
        phone: formData.phone,
        preferred_time: formData.preferredTime,
        notes: formData.notes,
        source: window.location.pathname,
      });
      setIsSubmitted(true);
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        handleDismiss();
      }, 3000);
    } catch (error) {
      console.error('Failed to submit callback request:', error);
      alert('Failed to submit request. Please try again or call us directly at +971 4 258 5754');
    }
    setIsSubmitting(false);
  };

  if (isDismissed || !isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={handleDismiss}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-[90vw] sm:max-w-sm bg-white shadow-2xl max-h-[85vh] overflow-y-auto rounded-xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="callback-popup"
        >
          {/* Mobile drag handle - hidden since we're centering now */}
          <div className="hidden w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3" />
          
          {/* Close Button */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 text-white/70 hover:text-white transition-colors z-10"
            aria-label="Close popup"
            data-testid="callback-close-btn"
          >
            <X weight="bold" className="w-5 h-5" />
          </button>

          {!isSubmitted ? (
            <>
              {/* Header - Compact on mobile */}
              <div className="bg-obsidian text-white px-4 sm:px-5 py-4 sm:py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 bg-bronze/20 flex items-center justify-center flex-shrink-0">
                    <Phone weight="fill" className="w-5 h-5 sm:w-6 sm:h-6 text-bronze" />
                  </div>
                  <div>
                    <h2 className="font-display text-base sm:text-lg font-bold">Request a Callback</h2>
                    <p className="font-body text-xs text-white/60">We'll call within 2 hours</p>
                  </div>
                </div>
              </div>

              {/* Form - Compact spacing on mobile */}
              <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-3 sm:space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-concrete mb-1.5">
                    Your Name *
                  </label>
                  <div className="relative">
                    <User weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-concrete" />
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your name"
                      className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-black/10 focus:border-obsidian focus:outline-none font-body text-sm transition-colors"
                      data-testid="callback-name-input"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-concrete mb-1.5">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-concrete" />
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+971 XX XXX XXXX"
                      className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-black/10 focus:border-obsidian focus:outline-none font-body text-sm transition-colors"
                      data-testid="callback-phone-input"
                    />
                  </div>
                </div>

                {/* Preferred Time - 2x2 grid */}
                <div>
                  <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-concrete mb-1.5">
                    <Clock weight="bold" className="inline w-3 h-3 mr-1" />
                    Preferred Time
                  </label>
                  <div className="grid grid-cols-4 sm:grid-cols-2 gap-1.5 sm:gap-2">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, preferredTime: slot.value })}
                        className={`px-2 py-2 sm:px-3 sm:py-2.5 text-[10px] sm:text-xs font-medium border transition-colors ${
                          formData.preferredTime === slot.value
                            ? 'bg-obsidian text-white border-obsidian'
                            : 'bg-white text-obsidian border-black/10 hover:border-obsidian'
                        }`}
                        data-testid={`callback-time-${slot.value}`}
                      >
                        <span className="sm:hidden">{slot.label}</span>
                        <span className="hidden sm:inline">{slot.fullLabel}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes - Hidden on mobile by default, shorter */}
                <div className="hidden sm:block">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-concrete mb-1.5">
                    Brief Note (Optional)
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="E.g., Interested in facade solutions..."
                    rows={2}
                    className="w-full px-4 py-2.5 border border-black/10 focus:border-obsidian focus:outline-none font-body text-sm transition-colors resize-none"
                    data-testid="callback-notes-input"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 sm:py-3.5 bg-bronze text-obsidian font-semibold uppercase tracking-wider text-xs sm:text-sm hover:bg-obsidian hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="callback-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Phone weight="bold" className="w-4 h-4" />
                      Request Callback
                    </>
                  )}
                </button>

                {/* Privacy Note */}
                <p className="text-[9px] sm:text-[10px] text-concrete text-center font-body">
                  Your data is protected and will not be shared.
                </p>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="p-6 sm:p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 15 }}
                className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
              >
                <CheckCircle weight="fill" className="w-7 h-7 sm:w-8 sm:h-8 text-green-500" />
              </motion.div>
              <h3 className="font-display text-lg sm:text-xl font-bold text-obsidian mb-2">Request Received!</h3>
              <p className="font-body text-sm text-concrete mb-3">
                We'll call you at <span className="font-semibold text-obsidian">{formData.phone}</span>
              </p>
              <p className="font-body text-xs text-concrete">
                Business Hours: Sun-Thu, 8AM - 6PM
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default CallbackPopup;
